import React from 'react';
import * as IoC from 'typescript-ioc';
import { FormattedMessage} from 'react-intl';
import { INotificationOptions, showNotification, closeEventNotifications } from '../../../../global/modal/alerts-host/alert-templates';
import { Routes } from '../../../../../routes-table';
import { Container } from 'typescript-ioc';
import { IRouter } from '../../../../../infrastructure/router';
import './show-report-toast.scss';
import { isPathIncludesInCurrentRoute } from '../../../../../infrastructure/utils/common.utils';
import { REPORT_GENERATION_COMPLETED_TEXT, REPORT_GENERATION_STARTED_TEXT } from '../report.texts';
import { DateFnsFormats } from '../../../../../infrastructure/date-formatter';
import { DateTimeFormatter } from '../../../../../infrastructure/implementations/date-formatter';
import { SuccessIcon } from '../../../../global/icons/icons';

export interface IReportGenerationNotificationProps {
    startDateTime?: Date;
    topic?: string;
    presentingOrganizationNames?: string[];
}

export function showReportGenerationCompletedToast(
    accountId: number,
    eventId: number,
    props: IReportGenerationNotificationProps,
) {
    const routingService = Container.get(IRouter);
    const currentRoute = routingService.router.getState();
    const isReportsTabOpen = isPathIncludesInCurrentRoute(Routes.Reports, currentRoute);
    const reportGeneratedMessage = hasNoNotificationProps(props)
        ? REPORT_GENERATION_COMPLETED_TEXT.defaultMessage
        : 'Report generation has completed for';

    const onLinkClick = () => {
        routingService.navigate(Routes.Reports, { accountId: accountId, eventId: eventId });
        closeEventNotifications();
    };

    const ReportLink = () => (
        <span className='report-toast-link' onClick={onLinkClick}>
            <FormattedMessage
                id='oe.reports.viewInReportsTab'
                description='Toast link label'
                defaultMessage='View in Reports tab'
            />
        </span>
    );

    const toastMessage = (
        <div className='report-generation-toast'>
            <span className='report-toast-title'>
                <SuccessIcon />
                <FormattedMessage {...REPORT_GENERATION_COMPLETED_TEXT} defaultMessage={reportGeneratedMessage}/>
            </span>
            {formReportGenerationToastMessage(props)}
            { !isReportsTabOpen && <ReportLink /> }
        </div>
    );

    showNotification(
        toastMessage,
        {
            withCloseIcon: !isReportsTabOpen,
            isRelatedToEvent: true,
            noStatusIcon: true,
        },
    );
}

export function showReportGenerationStartedToast(
    props: IReportGenerationNotificationProps,
    options: INotificationOptions = {},
) {
    const generateReportMessage = hasNoNotificationProps(props)
        ? REPORT_GENERATION_STARTED_TEXT.defaultMessage
        : REPORT_GENERATION_STARTED_TEXT.defaultMessage + ' for';

    const toastMessage = (
        <div className='report-generation-toast'>
            <span className='report-toast-title'>
                <FormattedMessage {...REPORT_GENERATION_STARTED_TEXT} defaultMessage={generateReportMessage}/>
            </span>
            {formReportGenerationToastMessage(props)}
        </div>
    );

    showNotification(toastMessage, {noStatusIcon: true, ...options});
}

function hasNoNotificationProps(props: IReportGenerationNotificationProps): boolean {
    return props && !props.startDateTime && !props.topic && !props.presentingOrganizationNames;
}

function formReportGenerationToastMessage(props: IReportGenerationNotificationProps): JSX.Element {
    return (
        <span className='report-toast-message'>
            {showStartDateTime(props.startDateTime)}
            {showTopic(props.topic)}
            {!props.topic ? showPresentingOrganizationNames(props.presentingOrganizationNames) : null}
        </span>
    );
}

function showStartDateTime(startDateTime: Date | undefined): JSX.Element {
    const dateTimeFormatter: DateTimeFormatter = IoC.Container.get(DateTimeFormatter);
    const formattedDate = startDateTime && dateTimeFormatter.dateToFormattedString(
        startDateTime,
        DateFnsFormats.StringifiedMonthDayYearTime
    );

    return formattedDate ?
        (<FormattedMessage
            id='oe.reports.startReportGenerationToast.startDate'
            defaultMessage='{startDateTime}'
            description='Toast message when report generation start date'
            values={{
                startDateTime: formattedDate
            }}
        />) : null;
}

function showTopic(topic: string | undefined): JSX.Element {
    return topic ? (
        <FormattedMessage
            id='oe.reports.startReportGenerationToast.topic'
            defaultMessage=', {topic}'
            description='Toast message when report generation topic'
            values={{
                topic: topic
            }}
        />
    ) : null;
}

function showPresentingOrganizationNames(presentingOrganizationNames: string[] | undefined): JSX.Element {
    return presentingOrganizationNames && presentingOrganizationNames.length > 0 ? (
        <FormattedMessage
            id='oe.reports.startReportGenerationToast.presentingOrganizationNames'
            defaultMessage=', {presentingOrganizationNames}'
            description='Toast message when report generation presenting organization names'
            values={{
                presentingOrganizationNames: presentingOrganizationNames.join(', ')
            }}
        />
    ) : null;
}
